import { ReactNode } from 'react';
import styled from '@emotion/styled';
import Swipe from 'react-easy-swipe';
import { __t } from '../../i18n';
import { IconClose } from '../../icons';
import { Icon, IconButton } from '../../styles';

type InfoSheetProps = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
};

/** Mobile stand-in for the desktop side panel: the same info, in a sheet over the player. */
export default function InfoSheet({ open, onClose, children }: InfoSheetProps) {
  return (
    <>
      <Backdrop open={open} onClick={onClose} />
      <Sheet open={open} role="dialog" aria-modal="true" aria-hidden={!open}>
        {/* Only the header grabs the swipe, so dragging inside a scrolled body doesn't dismiss. */}
        <SheetHeader tolerance={40} onSwipeDown={onClose}>
          <Grip />
          <IconButton variant="flat" round onClick={onClose} aria-label={__t('player.close')}>
            <Icon cursor="pointer"><IconClose /></Icon>
          </IconButton>
        </SheetHeader>
        <SheetBody>{children}</SheetBody>
      </Sheet>
    </>
  );
}

const Backdrop = styled.div<{ open: boolean }>(({ theme, open }) => ({
  position: 'fixed',
  inset: 0,
  zIndex: theme.zIndex.drawer,
  backgroundColor: theme.rgba(theme.palette.background[900][0], 0.6),
  opacity: open ? 1 : 0,
  pointerEvents: open ? 'auto' : 'none',
  transition: 'opacity 250ms ease',
}));

const Sheet = styled.div<{ open: boolean }>(({ theme, open }) => ({
  position: 'fixed',
  insetInline: 0,
  bottom: 0,
  zIndex: theme.zIndex.drawer + 1,
  display: 'flex',
  flexDirection: 'column',
  maxHeight: ['90vh', '90dvh'],
  borderRadius: `${theme.radius.full} ${theme.radius.full} 0 0`,
  backgroundColor: theme.palette.background[600][0],
  transform: `translateY(${open ? 0 : '100%'})`,
  // Hidden rather than unmounted so it animates in both directions; `visibility` also keeps it
  // out of the tab order while closed.
  visibility: open ? 'visible' : 'hidden',
  transition: 'transform 250ms ease, visibility 250ms',
}));

const SheetHeader = styled(Swipe)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'flex-end',
  flex: '0 0 auto',
  padding: theme.spacing(2, 2, 0),
  touchAction: 'pan-x',
}));

const Grip = styled.span(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  insetInline: 0,
  width: theme.spacing(6),
  height: 4,
  margin: '0 auto',
  borderRadius: theme.radius.pill,
  backgroundColor: theme.palette.background[300][0],
}));

const SheetBody = styled.div(({ theme }) => ({
  overflowY: 'auto',
  // Don't chain the scroll to the page once the body hits its end.
  overscrollBehavior: 'contain',
  WebkitOverflowScrolling: 'touch',
  padding: [
    theme.spacing(0, 3, 3),
    `0 ${theme.spacing(3)} calc(${theme.spacing(3)} + env(safe-area-inset-bottom, 0px))`,
  ],
}));
